import { DataProvider } from 'react-admin';
import { DeleteBranchParams, TreeRecord, TreeDataProvider } from '../../types';
import { deleteBranchByDeletingAllChildren } from './deleteBranchByDeletingAllChildren';
import { deleteBranchByDeletingNode } from './deleteBranchByDeletingNode';

/**
 * Provided the records contain a parent_id and a position field,
 * augment a dataProvider by adding the Tree methods (getTree, getRootNodes, etc).
 *
 * These methods will call regular dataProvider methods (getList, getOne, update).
 *
 * @warning Do not use in production.
 *
 * The added Tree methods call the API way too much, sometimes asking for the
 * entire tree. In practice, you should implement an optimized API route for
 * each of the Tree methods.
 *
 * @example // Compatible APIs should return records like
 * {
 *     id: 1234,
 *     name: 'hello',
 *     parent_id: 35,
 *     position: 4, // zero-based
 * }
 *
 * @param {DataProvider} dataProvider The dataProvider to augment
 * @param {string} parentIdField The name of the field containing the parent id. Defaults to 'parent_id'
 * @param {string} positionField The name of the field containing the position of a node inside its parent. Defaults to 'position'
 * @param {boolean} apiSupportBranchDeletion Indicates whether the API will handle children deletion when deleting a node as well as the siblings update. If false, the dataProvider will handle it by making multiple requests in the right order. Defaults to `false`.
 */
export const addTreeMethodsBasedOnParentAndPosition = <
    ResourceType extends string = string
>(
    dataProvider: DataProvider<ResourceType>,
    parentIdField = 'parent',
    positionField = 'position',
    apiSupportBranchDeletion = false
): DataProvider<ResourceType> & TreeDataProvider<ResourceType> => {
    const deleteBranch = apiSupportBranchDeletion
        ? deleteBranchByDeletingAllChildren
        : deleteBranchByDeletingNode;

    const treeDataProvider: TreeDataProvider<ResourceType> &
        DataProvider<ResourceType> = {
        ...dataProvider,

        getTree: async (resource, params) => {
            // get all records
            const { data } = await dataProvider.getList(resource, {
                filter: {},
                sort: { field: positionField, order: 'ASC' },
                pagination: { page: 1, perPage: 1000 },
                meta: params?.meta,
            });

            console.log(data);
            // fill children for each record
            // FIXME o(n2)
            const treeRecords = data.map(record => ({
                ...record,
                children: data
                    .filter(r => r[parentIdField]?.id == record.id)
                    .sort((a, b) => a[positionField] - b[positionField])
                    .map(child => child.id),
            }));
            return {
                data: treeRecords,
            };
        },

        getRootNodes: async (resource, params) => {
            // get root records
            const { data } = await dataProvider.getList(resource, {
                filter: { [parentIdField]: null },
                sort: { field: positionField, order: 'ASC' },
                pagination: { page: 1, perPage: 1000 },
                meta: params?.meta,
            });
            // fill children for each record
            const treeRecords = await Promise.all(
                data.map(record =>
                    dataProvider
                        .getList(resource, {
                            filter: { [`${parentIdField}.id`]: record.id },
                            sort: {
                                field: positionField,
                                order: 'ASC',
                            },
                            pagination: { page: 1, perPage: 1000 },
                            meta: params?.meta,
                        })
                        .then(({ data }) => ({
                            ...record,
                            children: data.map(({ id }) => id),
                        }))
                )
            );
            return {
                data: treeRecords,
            };
        },

        getParentNode: async <RecordType extends TreeRecord = TreeRecord>(
            resource,
            { childId, meta }
        ) => {
            const { data: tree } = await treeDataProvider.getTree<RecordType>(
                resource,
                { meta }
            );
            const child = tree.find(node => node.id === childId);

            if (!child) {
                return { data: undefined };
            }

            const parent = tree.find(node => node.id === child[parentIdField]);

            return {
                data: parent,
            };
        },

        getChildNodes: async (resource, { parentId, meta }) => {
            // get child records
            const { data } = await dataProvider.getList(resource, {
                filter: { [`${parentIdField}.id`]: parentId },
                sort: { field: positionField, order: 'ASC' },
                pagination: { page: 1, perPage: 1000 },
                meta,
            });
            // fill children for each record
            const treeRecords = await Promise.all(
                data.map(record =>
                    dataProvider
                        .getList(resource, {
                            filter: { [`${parentIdField}.id`]: record.id },
                            sort: {
                                field: positionField,
                                order: 'ASC',
                            },
                            pagination: { page: 1, perPage: 1000 },
                            meta,
                        })
                        .then(({ data }) => ({
                            ...record,
                            children: data.map(({ id }) => id),
                        }))
                )
            );
            return {
                data: treeRecords,
            };
        },

        moveAsNthChildOf: async (
            resource,
            { source, destination, position, meta }
        ) => {
            // 1. Increment the position of all destination child nodes after the position
            const destinationSiblings = (
                await dataProvider.getList(resource, {
                    filter: {
                        [`${parentIdField}.id`]: destination.id,
                        [`${positionField}.from`]: position,
                    },
                    sort: {
                        field: positionField,
                        order: 'ASC',
                    },
                    pagination: { page: 1, perPage: 1000 },
                    meta,
                })
            ).data;
            if (destinationSiblings.length > 0) {
                await Promise.all(
                    destinationSiblings.map(item =>
                        dataProvider.update(resource, {
                            id: item.id,
                            data: { 
                                [positionField]: item[positionField] + 1, 
                                [parentIdField]: item[parentIdField]?.id || null
                            },
                            previousData: item,
                            meta,
                        })
                    )
                );
            }
            // 2. Decrement the position of all nodes after the source node
            const sourceSiblings = (
                await dataProvider.getList(resource, {
                    filter: {
                        [`${parentIdField}.id`]: source[parentIdField]?.id || null,
                        [`${positionField}.from`]: source[positionField],
                    },
                    sort: {
                        field: positionField,
                        order: 'ASC',
                    },
                    pagination: { page: 1, perPage: 1000 },
                    meta,
                })
            ).data;

            if (sourceSiblings.length > 0) {
                await Promise.all(
                    sourceSiblings.map(item =>
                        dataProvider.update(resource, {
                            id: item.id,
                            data: { 
                                [positionField]: item[positionField] - 1,
                                [parentIdField]: item[parentIdField]?.id || null
                            },
                            previousData: item,
                            meta,
                        })
                    )
                );
            }
            // 3. Change the parent and position of the source node

            await dataProvider.update(resource, {
                id: source.id,
                data: {
                    [parentIdField]: destination.id,
                    [positionField]: position,
                },
                previousData: source,
                meta,
            });
            return { data: null };
        },

        moveAsNthSiblingOf: async (
            resource,
            { source, destination, position, meta }
        ) => {
            // 1. Increment the position of all nodes after the destination
            let destinationSiblingsToUpdateFilter;
            if (position === 0) {
                destinationSiblingsToUpdateFilter = {
                    [`${parentIdField}.id`]: destination[parentIdField]?.id || null,
                };
            }
            if (
                source[positionField] > destination[positionField] &&
                source[parentIdField]?.id === destination[parentIdField]?.id
            ) {
                destinationSiblingsToUpdateFilter = {
                    [`${parentIdField}.id`]: destination[parentIdField]?.id || null,
                    [`${positionField}.from`]: destination[positionField],
                };
            }
            if (
                source[parentIdField]?.id !== destination[parentIdField]?.id &&
                position > 0
            ) {
                destinationSiblingsToUpdateFilter = {
                    [`${parentIdField}.id`]: destination[parentIdField]?.id || null,
                    [`${positionField}.from`]: destination[positionField],
                };
            }
            if (destinationSiblingsToUpdateFilter) {
                const { data: destinationSiblings } =
                    await dataProvider.getList(resource, {
                        filter: destinationSiblingsToUpdateFilter,
                        sort: {
                            field: positionField,
                            order: 'ASC',
                        },
                        pagination: { page: 1, perPage: 1000 },
                        meta,
                    });

                if (destinationSiblings.length > 0) {
                    await Promise.all<any>(
                        destinationSiblings.map(item =>
                            item.id === source.id
                                ? Promise.resolve(undefined)
                                : dataProvider.update(resource, {
                                    id: item.id,
                                    data: { 
                                        [positionField]: item[positionField] + 1,
                                        [parentIdField]: item[parentIdField]?.id || null,
                                    },
                                    previousData: item,
                                    meta,
                                })
                        )
                    );
                }
            }
            // 2. Decrement the position of all nodes after the source node
            let sourceSiblingsToUpdateFilters;

            if (source[parentIdField]?.id === destination[parentIdField]?.id) {
                if (source[positionField] > destination[positionField]) {
                    sourceSiblingsToUpdateFilters = {
                        [`${parentIdField}.id`]: source[parentIdField]?.id || null,
                        [`${positionField}.from`]: source[positionField],
                    };
                } else {
                    sourceSiblingsToUpdateFilters = {
                        [`${parentIdField}.id`]: source[parentIdField]?.id || null,
                        [`${positionField}.from`]: source[positionField],
                        [`${positionField}.to`]: destination[positionField],
                    };
                }
            } else {
                sourceSiblingsToUpdateFilters = {
                    [`${parentIdField}.id`]: source[parentIdField]?.id || null,
                    [`${positionField}.from`]: source[positionField],
                };
            }
            const sourceSiblings = (
                await dataProvider.getList(resource, {
                    filter: sourceSiblingsToUpdateFilters,
                    sort: {
                        field: positionField,
                        order: 'ASC',
                    },
                    pagination: { page: 1, perPage: 1000 },
                    meta,
                })
            ).data;
            if (sourceSiblings.length > 0) {
                await Promise.all(
                    sourceSiblings.map(item =>
                        item.id === source.id
                            ? Promise.resolve(undefined)
                            : dataProvider.update(resource, {
                                id: item.id,
                                data: { 
                                    [positionField]: item[positionField] - 1,
                                    [parentIdField]: item[parentIdField]?.id || null
                                },
                                previousData: item,
                                meta,
                            })
                    )
                );
            }
            // 3. Change the parent and position of the source node
            await dataProvider.update(resource, {
                id: source.id,
                data: {
                    [parentIdField]: destination[parentIdField]?.id || null,
                    [positionField]:
                        source[parentIdField]?.id === destination[parentIdField]?.id
                            ? source[positionField] > destination[positionField]
                                ? destination[positionField] + 1
                                : destination[positionField]
                            : position,
                },
                previousData: source,
                meta,
            });
            return { data: null };
        },

        addRootNode: async (resource, { data, meta }) => {
            // get root records to compute position
            const { data: roots } = await dataProvider.getList(resource, {
                filter: { [parentIdField]: null },
                sort: { field: positionField, order: 'ASC' },
                pagination: { page: 1, perPage: 1000 },
                meta,
            });
            const rootPosition =
                roots.length > 0
                    ? roots.reduce(
                          (acc, curr) =>
                              curr[positionField] > acc
                                  ? curr[positionField]
                                  : acc,
                          -Infinity
                      ) + 1
                    : 0;
            const { data: newRoot } = await dataProvider.create(resource, {
                data: {
                    ...data,
                    [parentIdField]: null,
                    [positionField]: rootPosition,
                },
                meta,
            });
            return { data: { ...newRoot, children: [] } };
        },

        addChildNode: async (resource, { parentId, data, meta }) => {
            // get child records to compute position
            const { data: siblings } = await dataProvider.getList(resource, {
                filter: { [parentIdField]: parentId },
                sort: { field: positionField, order: 'ASC' },
                pagination: { page: 1, perPage: 1000 },
                meta,
            });
            const childPosition =
                siblings.length > 0
                    ? siblings.reduce(
                          (acc, curr) =>
                              curr[positionField] > acc
                                  ? curr[positionField]
                                  : acc,
                          -Infinity
                      ) + 1
                    : 0;
            const { data: newRoot } = await dataProvider.create(resource, {
                data: {
                    ...data,
                    [parentIdField]: parentId,
                    [positionField]: childPosition,
                },
                meta,
            });
            return { data: { ...newRoot, children: [] } };
        },

        deleteBranch: async <RecordType extends TreeRecord = TreeRecord>(
            resource: ResourceType,
            params: DeleteBranchParams<RecordType>
        ) => {
            const { id, previousData, meta } = params;
            // Deletion of the record itself must be handled by specialized method.
            await deleteBranch<ResourceType, RecordType>(
                treeDataProvider,
                resource,
                params
            );

            // However, we must update the node siblings position.

            // get the siblings
            const { data: siblings } =
                await treeDataProvider.getChildNodes<RecordType>(resource, {
                    parentId: previousData[parentIdField]?.id || null,
                    meta,
                });

            // update the siblings position
            await Promise.all(
                siblings
                    .filter(
                        node =>
                            node[positionField] > previousData[positionField] &&
                            node.id !== id
                    )
                    .map(node =>
                        dataProvider.update(resource, {
                            id: node.id,
                            data: {
                                [positionField]: node[positionField] - 1,
                                [parentIdField]: node[parentIdField]?.id || null
                            },
                            previousData: node,
                            meta,
                        })
                    )
            );

            return { data: previousData };
        },
    };

    return treeDataProvider;
};
